<template>
  <div class="main-content-container container-fluid px-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item"><a href="#" @click.prevent="$router.push({ name: 'insights' })">Insights</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit Insight' : 'Create Insight' }}</li>
      </ol>
    </nav>

    <!-- Page Header -->
    <div class="page-header row no-gutters py-4">
      <div class="col-12 col-sm-4 text-center text-sm-left mb-0">
        <h3 class="page-title">{{ isEditMode ? 'Edit Insight' : 'Create Insight' }}</h3>
      </div>
      <div class="col-12 col-sm-8 text-center text-sm-right">
        <d-button theme="secondary" @click="cancel">Cancel</d-button>
        <d-button theme="primary" @click="save" :disabled="saving">
          <i class="material-icons mr-1">save</i> {{ saving ? 'Saving...' : 'Save' }}
        </d-button>
      </div>
    </div>

    <!-- Insight Form -->
    <div class="row">
      <div class="col-lg-8">
        <div class="card card-small mb-4">
          <div class="card-header border-bottom">
            <h6 class="m-0">Insight Configuration</h6>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label for="insight-name">Name <span class="text-danger">*</span></label>
              <input
                id="insight-name"
                v-model="insight.name"
                type="text"
                class="form-control"
                placeholder="e.g., CTR by Scenario"
                required
              />
            </div>

            <div class="form-group">
              <label for="insight-description">Description</label>
              <textarea
                id="insight-description"
                v-model="insight.description"
                class="form-control"
                rows="3"
                placeholder="Describe what this insight shows..."
              />
            </div>

            <div class="form-group">
              <label for="insight-type">Visualization Type <span class="text-danger">*</span></label>
              <select id="insight-type" v-model="insight.type" class="form-control" required>
                <option value="">Select type...</option>
                <option value="bar_chart">Bar Chart</option>
                <option value="line_chart">Line Chart</option>
                <option value="area_chart">Area Chart</option>
                <option value="table">Table</option>
                <option value="top_items">Top Items</option>
                <option value="metric">Metric</option>
              </select>
            </div>

            <!-- Data Sources -->
            <div class="form-group">
              <label>Data Sources <span class="text-danger">*</span></label>
              <div v-for="(source, index) in insight.data_sources" :key="index" class="card mb-2">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <strong>Data Source {{ index + 1 }}</strong>
                    <d-button size="small" theme="danger" outline @click="removeDataSource(index)">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </div>
                  <div class="form-group">
                    <label>Type</label>
                    <select v-model="source.type" class="form-control">
                      <option value="recommendation_requests">Recommendation Requests</option>
                      <option value="recommended_items">Recommended Items</option>
                      <option value="detail_views">Detail Views</option>
                      <option value="purchases">Purchases</option>
                      <option value="carts">Carts</option>
                      <option value="detail_views_based_on_recommendations">Detail Views (Based on Recommendations)</option>
                      <option value="purchases_based_on_recommendations">Purchases (Based on Recommendations)</option>
                      <option value="carts_based_on_recommendations">Carts (Based on Recommendations)</option>
                      <option value="ratio">Ratio</option>
                    </select>
                  </div>
                  <div class="form-group" v-if="source.type !== 'ratio'">
                    <label>Scenario (Optional)</label>
                    <input v-model="source.scenario" type="text" class="form-control" placeholder="Filter by scenario name" />
                  </div>
                </div>
              </div>
              <d-button outline @click="addDataSource">
                <i class="material-icons">add</i> Add Data Source
              </d-button>
            </div>

            <!-- Filters -->
            <div class="form-group">
              <label>Filters (Optional)</label>
              <div v-for="(filter, index) in insight.filters" :key="index" class="card mb-2">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <strong>Filter {{ index + 1 }}</strong>
                    <d-button size="small" theme="danger" outline @click="removeFilter(index)">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </div>
                  <div class="form-group">
                    <label>Field</label>
                    <input v-model="filter.field" type="text" class="form-control" placeholder="e.g., scenario, item.category" />
                  </div>
                  <div class="form-group">
                    <label>Operator</label>
                    <select v-model="filter.operator" class="form-control">
                      <option value="equals">Equals</option>
                      <option value="contains">Contains</option>
                      <option value="in">In</option>
                      <option value="greater_than">Greater Than</option>
                      <option value="less_than">Less Than</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>Value</label>
                    <input v-model="filter.value" type="text" class="form-control" placeholder="Filter value" />
                  </div>
                </div>
              </div>
              <d-button outline @click="addFilter">
                <i class="material-icons">add</i> Add Filter
              </d-button>
            </div>

            <!-- Splits -->
            <div class="form-group">
              <label>Splits (Optional)</label>
              <div v-for="(split, index) in insight.splits" :key="index" class="card mb-2">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <strong>Split {{ index + 1 }}</strong>
                    <d-button size="small" theme="danger" outline @click="removeSplit(index)">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </div>
                  <div class="form-group">
                    <label>Field</label>
                    <input v-model="split.field" type="text" class="form-control" placeholder="e.g., scenario, item.category" />
                  </div>
                  <div class="form-group">
                    <label>Type</label>
                    <select v-model="split.type" class="form-control">
                      <option value="property">Property</option>
                      <option value="scenario">Scenario</option>
                      <option value="user">User</option>
                      <option value="item">Item</option>
                    </select>
                  </div>
                </div>
              </div>
              <d-button outline @click="addSplit">
                <i class="material-icons">add</i> Add Split
              </d-button>
            </div>

            <!-- Advanced Options -->
            <div class="form-group">
              <label>Sort Direction</label>
              <select v-model="insight.sort_direction" class="form-control">
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <div class="form-group">
              <label>Max Groups</label>
              <input v-model.number="insight.max_groups" type="number" class="form-control" min="1" max="100" />
            </div>

            <div class="form-group">
              <div class="form-check">
                <input v-model="insight.show_other_group" type="checkbox" class="form-check-input" id="show-other-group" />
                <label class="form-check-label" for="show-other-group">Show "Other" Group</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import insightsAPI from '@/api/insights';

export default {
  name: 'InsightEditor',
  data() {
    return {
      insight: {
        name: '',
        description: '',
        type: '',
        data_sources: [],
        filters: [],
        splits: [],
        sort_direction: 'desc',
        max_groups: 10,
        show_other_group: false,
      },
      saving: false,
    };
  },
  computed: {
    isEditMode() {
      return this.$route.name === 'insight_edit';
    },
  },
  created() {
    if (this.isEditMode) {
      this.loadInsight();
    } else {
      // Add default data source
      this.addDataSource();
    }
  },
  methods: {
    loadInsight() {
      // Extract ID from route - could be /insights/:id/edit or just :id
      const id = parseInt(this.$route.params.id);
      insightsAPI.getInsight(id)
        .then((response) => {
          const data = response.data;
          this.insight = {
            name: data.name || '',
            description: data.description || '',
            type: data.type || '',
            data_sources: data.data_sources || [],
            filters: data.filters || [],
            splits: data.splits || [],
            sort_direction: data.sort_direction || 'desc',
            max_groups: data.max_groups || 10,
            show_other_group: data.show_other_group || false,
          };
        })
        .catch((error) => {
          console.error('Failed to load insight:', error);
          alert('Failed to load insight: ' + (error.response && error.response.data && error.response.data.error || error.message));
        });
    },
    addDataSource() {
      this.insight.data_sources.push({
        type: 'recommendation_requests',
        scenario: '',
        filters: [],
      });
    },
    removeDataSource(index) {
      this.insight.data_sources.splice(index, 1);
    },
    addFilter() {
      this.insight.filters.push({
        field: '',
        operator: 'equals',
        value: '',
      });
    },
    removeFilter(index) {
      this.insight.filters.splice(index, 1);
    },
    addSplit() {
      this.insight.splits.push({
        field: '',
        type: 'property',
      });
    },
    removeSplit(index) {
      this.insight.splits.splice(index, 1);
    },
    save() {
      // Validate
      if (!this.insight.name || !this.insight.type) {
        alert('Please fill in all required fields');
        return;
      }
      if (!this.insight.data_sources || this.insight.data_sources.length === 0) {
        alert('Please add at least one data source');
        return;
      }

      this.saving = true;
      const promise = this.isEditMode
        ? insightsAPI.updateInsight(parseInt(this.$route.params.id), this.insight)
        : insightsAPI.createInsight(this.insight);

      promise
        .then((response) => {
          const id = response.data.id || parseInt(this.$route.params.id);
          this.$router.push({ name: 'insight_detail', params: { id } });
        })
        .catch((error) => {
          console.error('Failed to save insight:', error);
          alert('Failed to save insight: ' + (error.response && error.response.data && error.response.data.error || error.message));
        })
        .finally(() => {
          this.saving = false;
        });
    },
    cancel() {
      this.$router.push({ name: 'insights' });
    },
  },
};
</script>

